/**
 * Industry starter packs UI wording (tracker item 76): plain-language labels
 * for what a pack seeds, why a record is skipped or kept, and the routes.
 */

import * as types from '../api/types';

export const STARTER_PACKS_PATH = '/settings/starter-packs';
export const RECURRING_PRESETS_PATH = '/settings/recurring-presets';

/** What the pack is called in the singular (a record type) and in the plural, for counts. */
export const ENTITY_LABELS: Record<types.StarterPlanEntityType, [string, string]> = {
  ITEM: ['item', 'items'],
  ITEM_CATEGORY: ['item category', 'item categories'],
  PRICE_CHART: ['price chart', 'price charts'],
  CHECKLIST_TEMPLATE: ['checklist template', 'checklist templates'],
  LEAD_SOURCE: ['lead source', 'lead sources'],
  RECURRING_PRESET: ['recurring preset', 'recurring presets'],
  NOTIFICATION_TEMPLATE: ['message wording', 'message wordings'],
};

export function entityLabel(type: types.StarterPlanEntityType): string {
  return (ENTITY_LABELS[type] ?? [type, type])[0];
}

export function countLabel(count: number, type: types.StarterPlanEntityType): string {
  const [one, many] = ENTITY_LABELS[type] ?? [type, type];
  return `${count} ${count === 1 ? one : many}`;
}

/** Why a record is not created, in words an owner understands. */
export const SKIP_REASON_TEXT: Record<types.StarterSkipReason, string> = {
  EXISTS: 'Already in your account, so it is left as it is.',
  EXISTS_DELETED: 'You deleted an item with this name earlier. Deleted items keep their names, so it is not added again.',
  CUSTOMIZED: 'You changed this message wording, so your version is kept.',
  ACCOUNT_MISSING: 'The ledger account it needs is not in your chart of accounts. Add the account, then apply the pack again.',
};

export function skipReasonText(reason: types.StarterSkipReason | null): string {
  return reason ? SKIP_REASON_TEXT[reason] ?? reason : '';
}

/** Short form for tables */
export const SKIP_REASON_SHORT: Record<types.StarterSkipReason, string> = {
  EXISTS: 'Already exists',
  EXISTS_DELETED: 'Deleted earlier',
  CUSTOMIZED: 'You customized it',
  ACCOUNT_MISSING: 'Account missing',
};

export const KEEP_REASON_TEXT: Record<types.StarterKeepReason, string> = {
  EDITED: 'You changed it after it was added',
  IN_USE: 'It is used by your jobs, invoices, estimates or customers',
};

/** Items in the pack counts, as list lines ("12 items", ...), leaving out empty types. */
export function packCountLines(counts: types.StarterPackCounts): string[] {
  const rows: Array<[number, types.StarterPlanEntityType]> = [
    [counts.items, 'ITEM'],
    [counts.priceCharts, 'PRICE_CHART'],
    [counts.checklistTemplates, 'CHECKLIST_TEMPLATE'],
    [counts.recurringPresets, 'RECURRING_PRESET'],
    [counts.leadSources, 'LEAD_SOURCE'],
    [counts.notificationOverrides, 'NOTIFICATION_TEMPLATE'],
  ];
  return rows.filter(([n]) => n > 0).map(([n, type]) => countLabel(n, type));
}

/** Preview / apply entries grouped by record type, in the order the pack creates them. */
export const ENTRY_ORDER: types.StarterPlanEntityType[] = [
  'ITEM_CATEGORY', 'CHECKLIST_TEMPLATE', 'PRICE_CHART', 'ITEM', 'LEAD_SOURCE', 'RECURRING_PRESET', 'NOTIFICATION_TEMPLATE',
];

export function groupEntries<T extends { entityType: types.StarterPlanEntityType }>(entries: T[]): Array<[types.StarterPlanEntityType, T[]]> {
  return ENTRY_ORDER.map((type): [types.StarterPlanEntityType, T[]] => [type, entries.filter((e) => e.entityType === type)]).filter(([, rows]) => rows.length > 0);
}

export const PRICE_REVIEW_NOTE =
  'Prices in a starter pack are placeholders. Review every price, cost and price chart band before you quote or invoice a customer.';
